import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import {
	Checkbox,
	CollapsibleBlock,
	SubmissionButtons,
} from '@components/common';

import { useQueryAssetTypes } from '@hooks/assets/useQueryAssetTypes';

import { map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	applyFilterValues,
	resetAssetTypesFilter,
	resetRightSideControls,
	setAssetTypesFilter,
} from '@slices/map';

import { isEmpty } from '@utils/common';
import { getAssetTypeIdsWithSubtypes } from '@components/Map/MapControls/Controls/helpers';
import { transformAssetTypes } from '../helpers/transformAssetTypes';

import styles from './Tabs.module.scss';

const MAX_ROW_COUNT = 5;

const AssetTypes = () => {
	const dispatch = useAppDispatch();
	const {
		currentAssetsFilterValues,
		isApplyFiltersAvailable,
		pinnedFilter,
	} = useAppSelector(map);

	const { assetTypesSubtypes } = useQueryAssetTypes();

	const assetTypes = useMemo(() => transformAssetTypes(assetTypesSubtypes), [assetTypesSubtypes]);

	const { assetType: currentFilterValues } = currentAssetsFilterValues;

	useEffect(() => {
		if (isEmpty(currentFilterValues) && !isEmpty(assetTypes)) {
			dispatch(setAssetTypesFilter(assetTypes));
			setCollapsibleBlocksState(getAssetTypeIdsWithSubtypes(assetTypes));
		}
	}, [assetTypes, currentFilterValues, dispatch]);

	const [collapsibleBlocksState, setCollapsibleBlocksState] = useState<Record<string, boolean>>();

	const toggleCollapsibleBlocks = (id: string) => () => {
		setCollapsibleBlocksState(prevState => ({
			...prevState,
			[id]: !prevState?.[id],
		}));
	};

	const handleReset = () => {
		setCollapsibleBlocksState(getAssetTypeIdsWithSubtypes(assetTypes));

		dispatch(resetAssetTypesFilter());
		dispatch(applyFilterValues({}));

		if (!pinnedFilter) dispatch(resetRightSideControls());
	};

	const toggleParentType = (id: string) => () => {
		const preparedItems = currentFilterValues.map((item) => {
			if (item.id === id) {
				return {
					...item,
					isSelected: !item.isSelected,
					subTypes: item.subTypes?.map((subType) => ({
						...subType,
						isSelected: !item.isSelected,
					})),
				};
			}

			return item;
		});

		dispatch(setAssetTypesFilter(preparedItems));
	};

	const toggleAssetType = (parentTypeId: string, subtypeId?: string) => () => {
		const preparedItems = currentFilterValues.map((item) => {
			if (item.id === parentTypeId) {
				if (subtypeId) {
					return {
						...item,
						isSelected: true,
						subTypes: item.subTypes?.map((subType) => {
							if (subType.id === subtypeId) {
								return {
									...subType,
									isSelected: !subType.isSelected,
								};
							}

							return subType;
						}),
					};
				}

				return {
					...item,
					isSelected: !item.isSelected,
				};
			}

			return item;
		});

		dispatch(setAssetTypesFilter(preparedItems));
	};

	const applyFilters = () => {
		dispatch(applyFilterValues({}));

		if (!pinnedFilter) dispatch(resetRightSideControls());
	};

	return (
		<div className={styles.filterContainer}>
			{currentFilterValues.map(({
				name,
				iconURL,
				isSelected,
				className,
				subTypes,
				id,
				type,
			}) => {
				const collapsibleClassname = cn(styles.assetType, styles.collapsibleBlock, className, {
					[styles.inactiveIcon]: !isSelected,
				});
				const dataSelected = isSelected || subTypes?.some(({ isSelected }) => !!isSelected);
				const checkboxClassName = cn(styles.checkboxes, {
					[styles.singleColumn]: !(subTypes?.length && subTypes.length <= MAX_ROW_COUNT),
				});
				const itemClassname = cn(styles.assetType, className, {
					[styles.inactiveIcon]: !isSelected,
				});

				return (
					<div key={`${id}-${type}}`}>
						{subTypes?.length
							? (
								<div
									className={collapsibleClassname}
									data-selected={dataSelected}
									data-testid={`asset-parent-type-container-${name}`}
								>
									<CollapsibleBlock
										handleLabelClick={toggleParentType(id)}
										isClickableRow={false}
										isOpen={collapsibleBlocksState?.[id]}
										label={<>
											<div
												className={styles.iconContainer}
												style={{
													backgroundImage: `url(${iconURL})`,
													backgroundSize: 'cover',
												}}
											></div>
											<span>{name}</span>
										</>}
										name={name}
										toggleState={toggleCollapsibleBlocks(id)}
									>
										<div className={checkboxClassName}>
											{subTypes.map((item) => {
												const { name: checkboxName, isSelected, id: subtypeId } = item;

												return (
													<div
														key={subtypeId}
														className={styles.checkboxItem}
													>
														<Checkbox
															checked={isSelected}
															label={checkboxName}
															name={checkboxName}
															onChange={toggleAssetType(id, subtypeId)}
														/>
													</div>
												);
											})}
										</div>
									</CollapsibleBlock>
								</div>
							)
							: <div
								className={itemClassname}
								data-selected={isSelected}
								onClick={toggleAssetType(id)}
							>
								<div
									className={styles.iconContainer}
									style={{
										backgroundImage: `url(${iconURL})`,
										backgroundSize: 'cover',
									}}
								></div>
								<span>{name}</span>
							</div>}
					</div>
				);
			})}
			<SubmissionButtons
				className={styles.buttonsContainer}
				disabledPositive={!isApplyFiltersAvailable}
				negativeHandler={handleReset}
				negativeLabel='Reset all'
				positiveHandler={applyFilters}
				positiveLabel='Apply'
			/>
		</div>
	);
};

export default AssetTypes;
